import { useState, useEffect, useRef, useCallback, type FC, type KeyboardEvent } from 'react';
import { X, Calendar, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRescheduleBooking } from '../../hooks/useRescheduleBooking';
import { availabilityApi } from '../../api/availabilityApi';
import { cn } from '../../utils/cn';
import { useToast } from '../ui/Toast';
import type { BookingResponse, TimeSlot } from '../../types/booking.types';

interface RescheduleModalProps {
  booking: BookingResponse;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const RescheduleModal: FC<RescheduleModalProps> = ({ booking, isOpen, onClose, onSuccess }) => {
  const { isRescheduling, error, reschedule, clearError } = useRescheduleBooking();
  const { showToast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [focusedDate, setFocusedDate] = useState<Date | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const therapistId = booking.therapist?.id;
  const serviceId = booking.service?.id;

  // Format date for ARIA label (e.g., "Tuesday, January 15, 2025")
  const formatDateLabel = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Keyboard navigation handler for calendar
  const handleCalendarKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (!focusedDate) {
      // Initialize focus to first selectable date if not set
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setFocusedDate(today);
      return;
    }

    let newDate: Date | null = null;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newDate = new Date(focusedDate);
        newDate.setDate(focusedDate.getDate() - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        newDate = new Date(focusedDate);
        newDate.setDate(focusedDate.getDate() + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        newDate = new Date(focusedDate);
        newDate.setDate(focusedDate.getDate() - 7);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newDate = new Date(focusedDate);
        newDate.setDate(focusedDate.getDate() + 7);
        break;
      case 'Home':
        e.preventDefault();
        newDate = new Date(year, month, 1);
        break;
      case 'End':
        e.preventDefault();
        newDate = new Date(year, month + 1, 0);
        break;
      case 'PageUp':
        e.preventDefault();
        setCurrentMonth(new Date(year, month - 1));
        newDate = new Date(focusedDate);
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'PageDown':
        e.preventDefault();
        setCurrentMonth(new Date(year, month + 1));
        newDate = new Date(focusedDate);
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isDateSelectable(focusedDate)) {
          setSelectedDate(focusedDate);
        }
        return;
      default:
        return;
    }

    if (newDate) {
      // Update month if navigating outside current month
      if (newDate.getMonth() !== currentMonth.getMonth() || newDate.getFullYear() !== currentMonth.getFullYear()) {
        setCurrentMonth(new Date(newDate.getFullYear(), newDate.getMonth()));
      }
      setFocusedDate(newDate);
    }
  }, [focusedDate, currentMonth]);

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: (Date | null)[] = Array(firstDay.getDay()).fill(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const isDateSelectable = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  useEffect(() => {
    if (!selectedDate || !therapistId || !serviceId) return;
    const fetchSlots = async () => {
      setIsLoadingSlots(true);
      setSelectedTime(null);
      try {
        const dateStr = selectedDate.toISOString().split('T')[0] ?? '';
        const availability = await availabilityApi.getAvailability({
          therapistId,
          serviceId,
          date: dateStr,
        });
        setAvailableSlots(availability.slots.filter((s) => s.isAvailable));
      } catch {
        setAvailableSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate, therapistId, serviceId]);

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) return;
    const timeParts = selectedTime.split(':').map(Number);
    const hours = timeParts[0] ?? 0;
    const minutes = timeParts[1] ?? 0;
    const newStartTime = new Date(selectedDate);
    newStartTime.setHours(hours, minutes, 0, 0);
    const result = await reschedule(booking.id, newStartTime);
    if (result) {
      showToast({
        type: 'success',
        title: 'Booking Rescheduled',
        message: 'Your appointment has been rescheduled successfully.',
      });
      onSuccess();
      onClose();
    }
  };

  const handleClose = () => {
    clearError();
    setSelectedDate(null);
    setSelectedTime(null);
    onClose();
  };

  if (!isOpen) return null;

  const calendarDays = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Reschedule Booking</h2>
          <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg" aria-label="Close modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Current appointment</p>
            <p className="font-medium text-gray-900">{booking.service?.name}</p>
            <p className="text-sm text-gray-600">
              {new Date(booking.startTime).toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select New Date
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="p-1 hover:bg-gray-100 rounded" aria-label="Previous month">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm font-medium min-w-[120px] text-center">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="p-1 hover:bg-gray-100 rounded" aria-label="Next month">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div
              ref={calendarRef}
              role="grid"
              aria-label="Calendar"
              tabIndex={0}
              onKeyDown={handleCalendarKeyDown}
              onFocus={() => {
                if (!focusedDate) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  setFocusedDate(today);
                }
              }}
              className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
            >
              <div role="row" className="grid grid-cols-7 gap-1">
                {weekDays.map((day) => (
                  <div key={day} role="columnheader" className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              <div role="rowgroup" className="grid grid-cols-7 gap-1">
                {calendarDays.map((date, i) => {
                  const isSelected = selectedDate?.toDateString() === date?.toDateString();
                  const isFocused = focusedDate?.toDateString() === date?.toDateString();
                  const selectable = date && isDateSelectable(date);

                  return (
                    <div key={i} role="gridcell">
                      <button
                        type="button"
                        tabIndex={-1}
                        disabled={!selectable}
                        onClick={() => date && setSelectedDate(date)}
                        aria-label={date ? formatDateLabel(date) : undefined}
                        aria-selected={isSelected}
                        aria-disabled={!selectable}
                        className={cn(
                          'w-full p-2 text-sm rounded-lg transition-colors',
                          !date && 'invisible',
                          date && !isDateSelectable(date) && 'text-gray-300 cursor-not-allowed',
                          selectable && 'hover:bg-blue-50',
                          isSelected && 'bg-blue-600 text-white hover:bg-blue-700',
                          isFocused && !isSelected && 'ring-2 ring-blue-500 ring-inset'
                        )}
                      >
                        {date?.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {selectedDate && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" />
                Select New Time
              </h3>
              {isLoadingSlots ? (
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-10 bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              ) : availableSlots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.startTime}
                      onClick={() => setSelectedTime(slot.startTime)}
                      className={cn(
                        'px-3 py-2 text-sm rounded-lg border transition-colors',
                        selectedTime === slot.startTime
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-200 hover:border-blue-300'
                      )}>
                      {slot.startTime}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No available slots for this date</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <button onClick={handleClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleReschedule}
            disabled={!selectedDate || !selectedTime || isRescheduling}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
            {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;
